import { useRef } from 'react';
import { mockComments } from '@/mocks/contentData';

interface Comment {
  id: string;
  user: string;
  avatar: string;
  content: string;
  likes: number;
  time: string;
  replies: Reply[];
}

interface Reply {
  id: string;
  user: string;
  avatar: string;
  content: string;
  likes: number;
  time: string;
}

function ReplyItem({ reply }: { reply: Reply }) {
  return (
    <div className="flex gap-3 pl-10 py-3">
      <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
        <img src={reply.avatar} alt={reply.user} className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-foreground-300">{reply.user}</span>
          <span className="text-[10px] text-foreground-700">{reply.time}</span>
        </div>
        <p className="text-sm text-foreground-400 leading-relaxed mb-2">{reply.content}</p>
        <div className="flex items-center gap-4">
          <button className="flex items-center gap-1 text-xs text-foreground-600 hover:text-primary-400 transition-colors cursor-pointer">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-thumb-up-line"></i>
            </span>
            {reply.likes.toLocaleString()}
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentItem({ comment }: { comment: Comment }) {
  const [showReplies, setShowReplies] = useState(false);

  return (
    <div className="py-5 border-b border-background-200/60 last:border-0">
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
          <img src={comment.avatar} alt={comment.user} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-foreground-200">{comment.user}</span>
            <span className="text-xs text-foreground-700">{comment.time}</span>
          </div>
          <p className="text-sm text-foreground-400 leading-relaxed mb-3">{comment.content}</p>
          <div className="flex items-center gap-4">
            <button className="flex items-center gap-1 text-xs text-foreground-600 hover:text-primary-400 transition-colors cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-thumb-up-line"></i>
              </span>
              {comment.likes.toLocaleString()}
            </button>
            <button
              onClick={() => setShowReplies(!showReplies)}
              className="flex items-center gap-1 text-xs text-foreground-600 hover:text-primary-400 transition-colors cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-chat-3-line"></i>
              </span>
              回复 {comment.replies.length > 0 ? `(${comment.replies.length})` : ''}
            </button>
          </div>
        </div>
      </div>

      {/* Replies */}
      {showReplies && comment.replies.length > 0 && (
        <div className="mt-2">
          {comment.replies.map((reply) => (
            <ReplyItem key={reply.id} reply={reply} />
          ))}
        </div>
      )}
    </div>
  );
}

import { useState } from 'react';

export default function CommentSection({ videoId }: { videoId: string }) {
  const [commentText, setCommentText] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setIsSubmitted(true);
    setCommentText('');
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  return (
    <section className="py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/20">
          <i className="ri-chat-3-line text-primary-400 text-lg"></i>
        </div>
        <h3 className="text-lg font-bold text-foreground-100">评论 ({mockComments.length})</h3>
      </div>

      {/* Comment form */}
      <form onSubmit={handleSubmit} className="mb-8">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-background-200 flex items-center justify-center">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-user-line text-foreground-500 text-lg"></i>
            </span>
          </div>
          <div className="flex-1">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="说点什么吧，吃瓜不吐籽..."
              maxLength={500}
              rows={3}
              className="w-full bg-background-100 border border-background-200/70 rounded-xl px-4 py-3 text-sm text-foreground-300 placeholder:text-foreground-700 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-foreground-700">{commentText.length}/500</span>
              <button
                type="submit"
                disabled={!commentText.trim()}
                className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-700 text-background-950 text-sm font-semibold rounded-full transition-all duration-200 cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"
              >
                发表评论
              </button>
            </div>
            {isSubmitted && (
              <p className="text-xs text-accent-400 mt-2">评论发表成功！感谢你的参与 🍉</p>
            )}
          </div>
        </div>
      </form>

      {/* Comments list */}
      <div className="space-y-0">
        {mockComments.map((comment) => (
          <CommentItem key={comment.id} comment={comment} />
        ))}
      </div>
    </section>
  );
}